// DateRulesBox.tsx — v12.9.27 (#1045) · 2026-10-01
//
// THE DATE RULES / TIME RULES BOX OF THE FIELD DRAWER, exactly as UI-LOCK-12.9.27 v1.6 locks it:
//   * shown under Type when the type is Date (or Date of birth) or Time; nothing for any other type
//   * Allowed dates = three chips, multi-select, ONLY the ones the platform offers for this field's purpose
//     (hidden, not greyed - the 12.7.3 rule); at least one stays on
//   * Limits: "Earliest: [n] [unit] before today" / "Latest: [n] [unit] after today" on one line each; the
//     platform's limit is the ceiling (date of birth: 120 years) - a practice can tighten it, never loosen it
//   * Date order is NOT set per field: read-only, two rows, Country then Per language; a call looks up its
//     language first, then the country
//   * Time rules: one switch - "Not earlier than the moment the caller says it, when the date is today"
//   * Try it: posted to the CALL ENGINE (api date-try) so the answer is what a call would really do; nothing saved
//
// Tushar, 2026-10-01: "they can select only from what we are offering. it can be multi select."
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { HelpTooltip } from '../aiagent/HelpTooltip';

export interface DateRule {
  allowed?: string[];
  earliestN?: number | null;
  earliestUnit?: string;
  latestN?: number | null;
  latestUnit?: string;
  timeNotBeforeNow?: boolean | null;
  purpose?: string;
}
export interface DatePurposeInfo {
  purpose: string;
  isTime: boolean;
  offered: string[];
  default: DateRule;
  earliestMaxYears?: number | null;
}
export interface DateOrderInfo { country: string; countryOrder: string; languages: Record<string, string> }

const PURPOSE_LABEL: Record<string, string> = {
  BirthDate: 'Date of birth',
  NewAppointment: 'Appointment date',
  ExistingAppointment: 'Existing appointment',
  AppointmentTime: 'Appointment time',
  OtherTime: 'A time of day',
  Other: 'A date',
};
const CHIP: [string, string][] = [['past', 'Past'], ['today', 'Today'], ['future', 'Future']];
const LANG_NAME: Record<string, string> = { en: 'English', es: 'Spanish' };
const COUNTRY_NAME: Record<string, string> = { US: 'United States' };
const ORDER_WORDS: Record<string, string> = { mdy: 'month · day · year', dmy: 'day · month · year' };

export function isDateLikeType(t?: string) { return t === 'date' || t === 'birthdate' || t === 'time'; }

/** The purpose a field's rules are FOR - the server sends it for platform fields; a tenant's own field is a plain date or time. */
export function purposeFor(type?: string, served?: string) {
  if (served) return served;
  return type === 'time' ? 'OtherTime' : type === 'birthdate' ? 'BirthDate' : 'Other';
}

export function DateRulesBox(props: {
  fieldKey: string;
  type: string;
  purpose: string;
  purposes: DatePurposeInfo[];
  order?: DateOrderInfo;
  value?: DateRule | null;
  disabled?: boolean;
  onChange: (r: DateRule) => void;
}) {
  const { fieldKey, type, purpose, purposes, order, value, disabled, onChange } = props;
  const info = purposes.find((p) => p.purpose === purpose);
  const [heard, setHeard] = useState('');
  const [verdict, setVerdict] = useState<{ ok: boolean; outcome: string; says: string } | null>(null);
  if (!info) return null;

  const def = info.default ?? {};
  const allowed = (value?.allowed && value.allowed.length ? value.allowed : def.allowed ?? []).filter((a) => info.offered.includes(a));
  const earliestN = value?.earliestN ?? def.earliestN ?? null;
  const earliestUnit = value?.earliestUnit ?? def.earliestUnit ?? 'years';
  const latestN = value?.latestN ?? def.latestN ?? null;
  const latestUnit = value?.latestUnit ?? def.latestUnit ?? 'months';
  const same = value?.timeNotBeforeNow ?? def.timeNotBeforeNow ?? false;
  const cur: DateRule = { allowed, earliestN, earliestUnit, latestN, latestUnit, timeNotBeforeNow: info.isTime ? same : null };
  const set = (patch: Partial<DateRule>) => onChange({ ...cur, ...patch });
  const maxYears = info.earliestMaxYears ?? null;

  function clampEarliest(n: number | null, unit: string): number | null {
    if (n == null || !maxYears) return n;
    const days = unit === 'years' ? n * 366 : unit === 'months' ? n * 31 : n;
    if (days <= maxYears * 366) return n;
    return unit === 'years' ? maxYears : unit === 'months' ? maxYears * 12 : maxYears * 366;
  }

  return (
    <div className="aa-f dr-box">
      <h5 className="dr-h">{info.isTime ? 'TIME RULES' : 'DATE RULES'}</h5>
      <span className="aa-hint">
        For: <b>{PURPOSE_LABEL[purpose] ?? purpose}</b>{info.isTime
          ? <>, in the office&rsquo;s time zone.</>
          : <>. Checked on every path a date can come in by &mdash; the opening sentence, the step, corrections, Confirm, and before sending to a connected system.</>}
      </span>

      {!info.isTime && (
        <>
          <label>Allowed dates <HelpTooltip label="Allowed dates" sections={[['Allowed dates', 'Which dates a caller may give for this field. Pick one or more. Only the choices the platform allows for this purpose are shown.']]} /></label>
          <div className="dr-chips">
            {CHIP.filter(([v]) => info.offered.includes(v)).map(([v, l]) => {
              const on = allowed.includes(v);
              return (
                <button key={v} type="button" className={`dr-chip${on ? ' on' : ''}`} disabled={disabled}
                  onClick={() => {
                    const next = on ? allowed.filter((a) => a !== v) : [...allowed, v];
                    if (next.length === 0) return;   // at least one stays on
                    set({ allowed: CHIP.map(([c]) => c).filter((c) => next.includes(c)) });
                  }}>{l}</button>
              );
            })}
          </div>

          <label>Limits <HelpTooltip label="Limits" sections={[['Limits', 'Optional. How far back or ahead a date may be, counted from the day of the call. The platform’s limit is the most you can allow; you can only make it tighter.']]} /></label>
          {info.offered.includes('past') && (
            <div className="dr-lim">
              Earliest:
              <input type="number" min={1} value={earliestN ?? ''} placeholder="none" disabled={disabled}
                onChange={(e) => { const n = e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 1); set({ earliestN: clampEarliest(n, earliestUnit) }); }} />
              <select value={earliestUnit} disabled={disabled}
                onChange={(e) => set({ earliestUnit: e.target.value, earliestN: clampEarliest(earliestN, e.target.value) })}>
                <option value="years">years</option><option value="months">months</option><option value="days">days</option>
              </select>
              before today{maxYears ? <span className="aa-hint dr-inline">(platform limit: {maxYears} years)</span> : null}
            </div>
          )}
          {info.offered.includes('future') && (
            <div className="dr-lim">
              Latest:
              <input type="number" min={1} value={latestN ?? ''} placeholder="none" disabled={disabled}
                onChange={(e) => set({ latestN: e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 1) })} />
              <select value={latestUnit} disabled={disabled} onChange={(e) => set({ latestUnit: e.target.value })}>
                <option value="months">months</option><option value="years">years</option><option value="days">days</option>
              </select>
              after today <span className="aa-hint dr-inline">(e.g. 6 months; empty = no limit)</span>
            </div>
          )}

          <label>Date order <HelpTooltip label="Date order" sections={[['Date order', 'How an all-number date like 05/01/1990 is read. A call looks up its language first; a language with no order of its own uses the country. With a month word (“January 5”) the order never matters.']]} /></label>
          <div className="dr-order">
            <div className="dr-k">Country</div>
            <div className="dr-ro">{COUNTRY_NAME[order?.country ?? 'US'] ?? order?.country} &middot; {ORDER_WORDS[order?.countryOrder ?? 'mdy']} <span className="aa-hint dr-inline">(platform country)</span></div>
            <div className="dr-k">Per language</div>
            <div className="dr-ro">
              {Object.entries(order?.languages ?? {}).map(([l, o]) => (
                <div key={l}>{LANG_NAME[l] ?? l.toUpperCase()} &middot; {o ? ORDER_WORDS[o] ?? o : <i>uses the country</i>}</div>
              ))}
            </div>
          </div>
          <span className="aa-hint">Lookup: the call&rsquo;s language first, then the country.</span>
        </>
      )}

      {info.isTime && (
        <label className="dr-switch">
          <input type="checkbox" checked={!!same} disabled={disabled} onChange={(e) => set({ timeNotBeforeNow: e.target.checked })} />
          <span>Not earlier than the moment the caller says it, when the date is today</span>
          <HelpTooltip label="Same-day check" sections={[['Same-day check', 'Calling at 1:59 for 2:00 is fine. “2 PM” said at 2:01 is refused and the caller is asked for another time.']]} />
        </label>
      )}

      <TryIt fieldKey={fieldKey} type={type} rule={cur} heard={heard} setHeard={setHeard} verdict={verdict} setVerdict={setVerdict} isTime={info.isTime} />
    </div>
  );
}

function TryIt(props: {
  fieldKey: string; type: string; rule: DateRule; heard: string; isTime: boolean;
  setHeard: (s: string) => void;
  verdict: { ok: boolean; outcome: string; says: string } | null;
  setVerdict: (v: { ok: boolean; outcome: string; says: string } | null) => void;
}) {
  const { fieldKey, type, rule, heard, setHeard, verdict, setVerdict, isTime } = props;
  const ruleJson = JSON.stringify(rule);
  useEffect(() => {
    const h = heard.trim();
    if (!h) { setVerdict(null); return; }
    let live = true;
    const t = window.setTimeout(async () => {
      try {
        const r = await api.post<{ ok: boolean; outcome: string; says: string }>('/t2/settings/organization/date-try',
          { key: fieldKey, type, rule: JSON.parse(ruleJson), heard: h, language: 'en' });
        if (live) setVerdict(r);
      } catch { if (live) setVerdict({ ok: false, outcome: 'Could not reach the server to try it.', says: '' }); }
    }, 350);
    return () => { live = false; window.clearTimeout(t); };
  }, [heard, fieldKey, type, ruleJson, setVerdict]);
  return (
    <div className="dr-try">
      <label>Try it <HelpTooltip label="Try it" sections={[['Try it', 'Type what a caller might say. Shows what the call would do with the rules above, as they are now. Nothing is saved.']]} /></label>
      <input value={heard} onChange={(e) => setHeard(e.target.value)}
        placeholder={isTime ? 'e.g. 2 PM' : 'e.g. January 5, 1990 · 115 1990 · next Tuesday'} />
      {verdict && (
        <div className={`dr-verdict ${verdict.ok ? 'ok' : 'no'}`}>
          {verdict.outcome}{verdict.says ? <> &mdash; the agent says: &ldquo;{verdict.says}&rdquo;</> : null}
        </div>
      )}
    </div>
  );
}
